import { differenceInDays, endOfMonth, endOfWeek, startOfMonth, startOfWeek, startOfYear, sub, subDays } from "date-fns"
import { NIcon, NTag, NText, type SelectOption } from "naive-ui"
import type { Component, HTMLAttributes, VNodeChild, Ref } from "vue"
import type { LocationQueryRaw } from "vue-router"
import type { InsightsPeriod, InvoiceParameter } from "./types"
import { DateOnly } from "./classes"
import { debounce } from "lodash"

export function formatNumber(value: unknown, minimumFractionDigits: number = 0, showSign = false): string {
  if (typeof value !== 'string' && typeof value !== 'number') {
    return ''
  }  

  return new Intl.NumberFormat('da-DK', { maximumFractionDigits: 5, minimumFractionDigits, signDisplay: showSign ? 'always' : 'auto' }).format(Number(value))
}

export function enumToKeys(enumToConvert: object): string[] {
  return Object.keys(enumToConvert).filter(key => isNaN(Number(key)))
}

export function enumToSelectOptions(enumToConvert: object): SelectOption[] {
  return Object.keys(enumToConvert).filter(key => isNaN(Number(key))).map(x => ({ label: toTitleCase(x), value: x }))
}

export function renderIcon(icon: Component) {
  return () => h(NIcon, null, {
    default: () => h(icon)
  })
}

export function isActive(item: InvoiceParameter): boolean {
  const now = new Date()
  return new Date(item.validFrom) < now && (!item.validTo || new Date(item.validTo) > now)
}

export function renderStatus(item: InvoiceParameter): VNodeChild | null {
  if (isActive(item)) {
    return h(NTag, { type: 'success', size: 'small', bordered: true }, () => 'Active')
  }
  else return null
}

export function renderMultilineOption(label: string, description?: string): VNodeChild {
  return h('div', {
    class: 'flex flex-col'
  }, [
    label,
    description
      ? h(NText, { depth: 3, tag: 'div' }, () => description)
      : null
  ])
}

export function toTitleCase(input?: string | null): string {
  if (!input) return ''

  // Insert a space before each uppercase letter (except the first one)
  const result = input.replace(/([a-z])([A-Z])/g, '$1 $2');
  
  // Convert to lowercase and then capitalize the first letter of the first word
  return result.charAt(0).toUpperCase() + result.slice(1).toLowerCase();
}

export function clickableRowProps<T>(onRowClick?: (row: T) => void): (row: T, index: number) => HTMLAttributes {
  return (row: T) => ({
    style: { cursor: 'pointer' },
    onClick: () => onRowClick?.(row)
  })
}

/**
 * Removes all keys from an object where the value is null or undefined
 * @param obj - The input object to filter
 * @returns A new object with null/undefined values removed
 */
export function removeNullishKeys<T extends Record<string, any>>(
  obj: T
): Partial<T> {
  const result: Partial<T> = {};
  
  for (const [key, value] of Object.entries(obj)) {
    if (value !== null && value !== undefined && value.length !== 0) {
      result[key as keyof T] = value;
    }
  }
  
  return result;
}

export function createDebouncedSetter<T>(target: Ref<T>, delay = 300) {
  return debounce((value: T) => {
    target.value = value
  }, delay)
}

export function encodeQuery(obj: object): LocationQueryRaw {
  let query: LocationQueryRaw = {}

  for (const [key, value] of Object.entries(obj)) {
    if (Array.isArray(value)) {
      query[key] = value
    }
    else if (typeof value === 'object') query = { ...query, ...value }
    else query[key] = value
  }

  return query
}

export function parseInterval(interval: InsightsPeriod): { fromDate: DateOnly, toDate: DateOnly } | undefined {
  if (Array.isArray(interval)) {
    const from = new DateOnly(interval[0])
    const to = new DateOnly(interval[1])

    return { fromDate: from, toDate: to }
  }

  else if (typeof interval === 'string') {
    switch (interval) {
      case 'yesterday': {
        const yesterday = DateOnly.fromDate(sub(new Date(), { days: 1 }))
        return { fromDate: yesterday, toDate: yesterday }
      }
      case 'lastWeek': {
        const from = DateOnly.fromDate(startOfWeek(sub(new Date(), { weeks: 1 }), { weekStartsOn: 1 }))
        const to = DateOnly.fromDate(endOfWeek(sub(new Date(), { weeks: 1 }), { weekStartsOn: 1 }))
        return { fromDate: from, toDate: to }
      }
      case 'lastMonth': {
        const from = DateOnly.fromDate(startOfMonth(sub(new Date(), { months: 1 })))
        const to = DateOnly.fromDate(endOfMonth(sub(new Date(), { months: 1 })))
        return { fromDate: from, toDate: to }
      }
      case 'ytd': {
        const from = DateOnly.fromDate(startOfYear(new Date()))
        const to = DateOnly.fromDate(new Date())
        return { fromDate: from, toDate: to }
      }
      default: {
        const match = interval.match(/^(\d+)([dhwmy])$/);
        if (!match) return undefined;

        const to = DateOnly.today()
        const [, value, unit] = match;
        const num = parseInt(value);
        
        const units: Record<string, object> = {
          h: { hours: num },
          d: { days: num },
          w: { weeks: num },
          m: { months: num },
          y: { years: num }
        };

        const from = DateOnly.fromDate(sub(new Date(), units[unit]))
        
        return { fromDate: from, toDate: to };
      }
    }
  }  
}

export function getPreviousInterval(fromDate: DateOnly, toDate: DateOnly): { fromDate: DateOnly, toDate: DateOnly } {
  const end = subDays(fromDate.toDate(), 1)
  const difference = differenceInDays(toDate.toDate(), fromDate.toDate())
  const start = subDays(end, difference + 1)

  return { fromDate: DateOnly.fromDate(start), toDate: DateOnly.fromDate(end) }
}

export function showErrorMessage(text: string = 'Something went wrong!') {
  const message = useMessage()

  message.error(text, { duration: 3000 })
}